import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useSeedbotHealth } from './useSeedbotHealth'

export interface SeedbotLaunch {
  name: string
  symbol: string
  creator_address: string
  creator_username: string
  created_at: string
  mint_address: string
  image_url: string | null
}

export function useSeedbotLaunches() {
  const { data: healthData } = useSeedbotHealth()

  // Get wallet addresses from health endpoint
  const walletAddresses = healthData?.wallets.map((w) => w.address) ?? []

  return useQuery({
    queryKey: ['seedbot-launches', walletAddresses],
    queryFn: async (): Promise<SeedbotLaunch[]> => {
      // Fetch launches and creator profiles in parallel
      const [tokensResult, profilesResult] = await Promise.all([
        supabase
          .from('launched_tokens')
          .select('name, symbol, creator_address, created_at, mint_address, image_url')
          .in('creator_address', walletAddresses)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('creator_profiles')
          .select('wallet_address, username')
          .in('wallet_address', walletAddresses),
      ])

      if (tokensResult.error) throw tokensResult.error
      if (!tokensResult.data) return []

      // Build wallet -> username lookup
      const usernameMap = new Map<string, string>()
      if (profilesResult.data) {
        for (const profile of profilesResult.data) {
          usernameMap.set(profile.wallet_address, profile.username)
        }
      }

      return tokensResult.data.map((token) => ({
        ...token,
        creator_username: usernameMap.get(token.creator_address) || 'unknown',
      }))
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    enabled: walletAddresses.length > 0,
  })
}
